import { NgTemplateOutlet } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	inject,
	OnInit,
	signal,
} from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import {
	CentsToDollarsPipe,
	DialogBase,
	DialogOptions,
	TimestampPipe,
} from '@marmon/frontend/common';
import { LoggerService } from '@marmon/frontend/logging';
import { AlgoliaAnalysis, Analysis } from '@marmon/shared/types/analysis';
import { User } from '@marmon/shared/types/user';
import { TuiSkeleton } from '@taiga-ui/kit';
import { AnalysisApiService } from '../../services';

@Component({
	selector: 'mar-analysis-details-dialog',
	standalone: true,
	imports: [
		TranslocoPipe,
		NgTemplateOutlet,
		CentsToDollarsPipe,
		TimestampPipe,
		TuiSkeleton,
	],
	templateUrl: 'analysis-details-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: { class: 'relative block' },
})
export class AnalysisDetailsDialogComponent
	extends DialogBase<void, { analysis: AlgoliaAnalysis }>
	implements OnInit
{
	static override DialogOptions = {
		size: 'm',
		header: 'analyses.dialog.analysis-details.header',
	} as DialogOptions;

	private readonly analysisApiService = inject(AnalysisApiService);
	private readonly logger = inject(LoggerService);

	readonly analysis = signal<Analysis | null>(null);
	readonly owner = signal<User | null>(null);
	readonly loading = signal<boolean>(true);

	ngOnInit() {
		this.fetchAnalysis();
	}

	async fetchAnalysis() {
		this.loading.set(true);
		try {
			const analysis = await this.analysisApiService.findById(
				this.context.data.analysis.objectID,
			);
			const owner = await this.analysisApiService.findCreator(analysis);

			this.analysis.set(analysis);
			this.owner.set(owner);
		} catch (error) {
			this.logger.captureException(error as Error, {
				source: 'AnalysisDetailsDialog.fetchAnalysis',
			});
			this.onDismiss();
		} finally {
			this.loading.set(false);
		}
	}
}
